import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Avatar,
  IconButton,
  Icons,
  Divider,
} from '@ublend-npm/aulaui-core';
import { Dialog, CircularProgress } from '@mui/material';
import { useTheme } from '@ublend-npm/aulaui-core';
import { useDispatch, useSelector, connect } from 'react-redux';
import TimeAgo from 'react-timeago';
import { fetchSpacePost } from '@core/actions/space/posts';
import { tryToSubscribeToSpaceLevelEvents } from '@core/actions/socket';
import { getPostById } from '@core/selectors/posts.selectors';
import { getComments } from '@core/selectors/comments';
import CommentsEntityTypes from '@core/utils/commentsEntityTypes';
import {
  accessingSpaceAsStaff,
  isInstructorInSpace,
} from '@core/selectors/classroom';
import {
  currentUserId as getCurrentUserId,
  accessToken as getAccessToken,
  currentUser,
} from '@core/selectors/user';
import { getMaterialPlacementLtiProviders } from '@core/selectors/lti';
import { operations as reactionOperations } from '@app/store/reactions';
import { HOME_UPDATE_POST_COUNTS } from '@core/constants/actions';
import { COMMON_ROOM } from '@core/constants/postFeedType';

import Reactions from '@components/presentational/Reactions/Reactions.container';
import CommentListCollapsable from '@components/presentational/Comments/CommentListCollapsable/CommentListCollapsable';
import CreateComment from '@components/presentational/Comments/CreateComment/CreateComment';
import AulaEditorWithAnalytics from '@components/presentational/common/AulaEditor/AulaEditorWithAnalytics';
import AulaEditorWithAnalyticsWithoutPlugins from '@components/presentational/common/AulaEditor/AulaEditorWithAnalyticsWithoutPlugins';
import EditorReadOnly from '@components/presentational/common/EditorPlus/EditorReadOnly';

// Custom time formatter matching app styling
const timeFormatter = (value: number, unit: string, suffix: string) => {
  if (value <= 60 && unit === 'second') {
    return 'Just now';
  }
  let unitPluralized = unit;
  if (value !== 1) {
    unitPluralized += 's';
  }
  return `${value} ${unitPluralized} ${suffix}`;
};

// --- Custom CreateComment container for Home context ---
// Injects spaceId from prop instead of reading currentClassRoomId
const mapCreateCommentStateToProps = (state: any, ownProps: any) => {
  const { spaceId } = ownProps;
  const userId = getCurrentUserId(state);
  const isEducator = isInstructorInSpace(state)(userId, spaceId);

  return {
    user: state.user.user,
    isCreatingComment: state.comment.isCreatingComment,
    spaceId,
    isEducator,
    accessToken: getAccessToken(state, spaceId),
    institution: state.institution.general.institution,
    ltiProviders: getMaterialPlacementLtiProviders(state),
    EditorComponent: isEducator
      ? AulaEditorWithAnalytics
      : AulaEditorWithAnalyticsWithoutPlugins,
  };
};

const HomeCreateComment = connect(mapCreateCommentStateToProps)(CreateComment);

// --- Custom EditorReadOnly container for Home context ---
// Injects spaceId from prop instead of reading state.classRoom.current
const mapEditorReadOnlyStateToProps = (state: any, ownProps: any) => {
  const { spaceId } = ownProps;
  const user = currentUser(state);
  const userId = state.user?.userId;
  const isEducator = isInstructorInSpace(state)(userId, spaceId);

  return {
    accessToken: getAccessToken(state, spaceId),
    institution: state.institution.general.institution,
    spaceId,
    user,
    isEducator,
    ltiProviders: getMaterialPlacementLtiProviders(state),
  };
};

const HomeEditorReadOnly = connect(mapEditorReadOnlyStateToProps)(
  EditorReadOnly
);

export interface PostDetailPopupProps {
  item: any;
  open: boolean;
  onClose: () => void;
}

const PostDetailPopup: React.FC<PostDetailPopupProps> = ({
  item,
  open,
  onClose,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [showComments, setShowComments] = useState(false);

  const spaceId = item?.classRoom;
  const postId = item?.objectId || item?.id;

  // Get hydrated post from state.data.posts (populated by fetchSpacePost)
  const post = useSelector((state: any) =>
    postId ? getPostById(state, postId) : null
  );

  // Get comments from state.data (real-time updated)
  const comments = useSelector((state: any) =>
    postId ? getComments(state, postId, CommentsEntityTypes.post) : []
  );

  // Check if user is read-only (staff) in this post's space
  const isReadOnly = useSelector((state: any) =>
    spaceId ? accessingSpaceAsStaff(state)(spaceId) : false
  );

  // Get reactions count from the Redux reactions store for syncing
  const reactionsFromStore = useSelector((state: any) => {
    if (!postId) return [];
    const reactionMap = state.data?.reactions || {};
    return Object.values(reactionMap).filter((r: any) => r.itemId === postId);
  });

  // Fetch post & subscribe to space events when popup opens
  useEffect(() => {
    if (open && postId && spaceId) {
      setIsLoading(true);
      const loadPost = async () => {
        try {
          await dispatch(fetchSpacePost(postId, true) as any);
          dispatch(tryToSubscribeToSpaceLevelEvents(spaceId) as any);
        } catch (err) {
          console.error('Failed to load post details', err);
        } finally {
          setIsLoading(false);
        }
      };
      loadPost();
    }
  }, [open, postId, spaceId, dispatch]);

  // Sync counts back to home.posts on close
  const handleClose = useCallback(() => {
    if (postId) {
      const commentsCount = comments?.length || 0;
      // Sum up reactions - from Redux store
      const reactionsCount = reactionsFromStore.reduce(
        (sum: number, r: any) =>
          sum + (typeof r.count === 'number' ? r.count : 1),
        0
      );

      dispatch({
        type: HOME_UPDATE_POST_COUNTS,
        postId,
        commentsCount,
        reactionsCount,
      });
    }
    setShowComments(false);
    onClose();
  }, [postId, comments, reactionsFromStore, dispatch, onClose]);

  const toggleReaction = ({ emojiName }: { emojiName: string }) =>
    dispatch(
      reactionOperations.toggleReaction({
        emojiName,
        itemId: postId,
        itemType: 'UBClassRoomPost',
      })
    );

  // Author info from the home post item
  const firstName = item?.user?.firstName || 'User';
  const lastName = item?.user?.lastName || '';
  const fullName = `${firstName} ${lastName}`.trim();
  const avatarUrl = item?.user?.avatar || '';
  const userInitials = `${firstName.charAt(0)}${lastName.charAt(
    0
  )}`.toUpperCase();
  const feedLabel =
    item?.feedType === 'commonRoom' ? 'Common Room' : 'Announcements';

  const isDark = theme.palette.mode === 'dark';
  const dialogBg = isDark ? '#1e1e1e' : '#ffffff';

  // Parse content
  const content = post?.content ? JSON.parse(post.content) : null;

  // Classroom info
  const classRoomsMap = useSelector(
    (state: any) => state.data?.classRooms || {}
  );
  const matchedClassRoom = Object.values(classRoomsMap).find(
    (room: any) =>
      room.objectId === item?.classRoom || room.id === item?.classRoom
  ) as any;
  const classRoomName = matchedClassRoom?.name;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="post-detail-title"
      PaperProps={{
        sx: {
          bgcolor: dialogBg,
          borderRadius: 3,
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 3,
          pt: 2.5,
          pb: 1.5,
          borderBottom: `1px solid ${theme.palette.divider}`,
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography
            id="post-detail-title"
            variant="subtitle1"
            fontWeight="bold"
            color="textPrimary"
          >
            {feedLabel}
          </Typography>
          {classRoomName && (
            <Typography variant="caption" color="textSecondary">
              {classRoomName}
            </Typography>
          )}
        </Box>
        <IconButton
          onClick={handleClose}
          aria-label="Close post detail"
          size="small"
          sx={{
            color: theme.palette.text.secondary,
            '&:hover': {
              color: theme.palette.text.primary,
            },
          }}
        >
          <Icons.Close fontSize="small" />
        </IconButton>
      </Box>

      {/* Content Area */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: 3,
          py: 2,
        }}
      >
        {isLoading || !post || !content ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 200,
            }}
          >
            <CircularProgress size={32} />
          </Box>
        ) : (
          <>
            {/* Author Banner */}
            <Box display="flex" alignItems="center" gap={1.5} mb={2}>
              <Avatar
                src={avatarUrl}
                fallbackText={userInitials}
                sx={{ width: 44, height: 44 }}
                aria-hidden="true"
              />
              <Box>
                <Typography
                  variant="body1"
                  fontWeight="medium"
                  color="textPrimary"
                >
                  {fullName}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  <TimeAgo date={item?.createdAt} formatter={timeFormatter} />
                </Typography>
              </Box>
            </Box>

            {/* Post Content - rendered with the full editor */}
            <Box sx={{ mb: 2 }}>
              <HomeEditorReadOnly
                key={post.editedAt || post.createdAt}
                itemId={post.objectId}
                rawEditorState={content}
                itemType={post.feedType || COMMON_ROOM}
                spaceId={spaceId}
              />
              {post.editedAt && (
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{ mt: 0.5, display: 'block' }}
                >
                  (Edited)
                </Typography>
              )}
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* Reactions */}
            <Reactions
              sequenceId={`home-post-${postId}`}
              itemId={postId}
              toggleReaction={toggleReaction}
              style={{ marginBottom: 15 }}
              readOnly={isReadOnly}
            />

            {/* Comments */}
            <Box sx={{ width: '100%' }}>
              <CommentListCollapsable
                entityId={postId}
                entityType={CommentsEntityTypes.post}
                itemType={post.feedType || COMMON_ROOM}
                showComments={showComments}
                showInitialComments
                comments={comments}
                commentsCount={comments.length}
                spaceId={spaceId}
              />
              {!isReadOnly && postId && (
                <HomeCreateComment
                  entityId={postId}
                  entityType={CommentsEntityTypes.post}
                  didComment={() => setShowComments(true)}
                  spaceId={spaceId}
                />
              )}
            </Box>
          </>
        )}
      </Box>
    </Dialog>
  );
};

export default PostDetailPopup;
